import { InformationCircleIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useState } from "react";

import { AutoComplete } from "@/components/AutoCompleteInput";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useGetWorkspaceMembers } from "@/queries/workspace";
import { useSetWorkspaceFallbackOwner } from "@/queries/identity/useSetWorkspaceFallbackOwner";
import { Workspace } from "@/types/workspaces";
import { displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface WorkspaceMember {
  user_id: number;
  user?: { id: number; email: string; name?: string };
  email?: string;
  name?: string;
}

const NO_FALLBACK_OWNER_ID = "none";

const FALLBACK_OWNER_HELP =
  "When a user is deprovisioned, ownership of their workspace entities transfers to this user first. If they are also gone, ownership falls back to the org Owner with workspace Admin, then the oldest workspace Admin.";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspace: Workspace | null;
}

/**
 * Per-workspace fallback owner picker. The selected user becomes the
 * first link in the ownership-transfer chain when an existing owner is
 * deprovisioned. The backend enforces workspace Admin permission.
 */
export const WorkspaceFallbackOwnerDialog = ({ open, onOpenChange, workspace }: Props) => {
  const workspaceId = workspace?.id;
  const { data: membersResp } = useGetWorkspaceMembers(
    workspaceId ? String(workspaceId) : "",
    Boolean(workspaceId),
  );
  const mutation = useSetWorkspaceFallbackOwner(workspaceId ?? 0);

  const initialOwner = (workspace as Workspace & { fallback_owner_user_id?: number | null })
    ?.fallback_owner_user_id;
  const [selectedUserId, setSelectedUserId] = useState<number | null>(initialOwner ?? null);

  useEffect(() => {
    if (open) {
      setSelectedUserId(initialOwner ?? null);
    }
  }, [open, initialOwner]);

  const members: WorkspaceMember[] = useMemo(
    () => (membersResp?.members as WorkspaceMember[]) ?? [],
    [membersResp],
  );

  const memberOptions = useMemo(
    () => [
      {
        id: NO_FALLBACK_OWNER_ID,
        value: NO_FALLBACK_OWNER_ID,
        label: "No fallback owner (use chain)",
      },
      ...members.map(member => {
        const id = member.user?.id ?? member.user_id;
        const email = member.user?.email ?? member.email ?? `user_${id}`;
        return {
          id: String(id),
          value: String(id),
          label: email,
        };
      }),
    ],
    [members],
  );

  const selectedValue = selectedUserId !== null ? String(selectedUserId) : NO_FALLBACK_OWNER_ID;

  const handleSave = () => {
    if (!workspaceId) {
      return;
    }
    mutation.mutate(
      { fallback_owner_user_id: selectedUserId },
      {
        onSuccess: () => {
          displayToast("Fallback owner saved.", ToastType.success);
          onOpenChange(false);
        },
        onError: () => displayToast("Failed to save fallback owner.", ToastType.error),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Workspace fallback owner
            <BasicTooltip content={<p className="max-w-xs text-xs">{FALLBACK_OWNER_HELP}</p>}>
              <InformationCircleIcon className="h-4 w-4 text-gray-500" aria-hidden />
            </BasicTooltip>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <AutoComplete
            options={memberOptions}
            value={selectedValue}
            onSelect={value =>
              setSelectedUserId(!value || value === NO_FALLBACK_OWNER_ID ? null : Number(value))
            }
            addNewOption={false}
            placeholder="Select fallback owner..."
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={mutation.isLoading}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
